import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Alert } from '../models/alert-class';
import {
  AuxClaimsPolicyNumbersClass,
  InsuranceClaimsCausesTypesClass,
  InsuranceclaimsClass,
  InsuranceClaimsCommentListClass,
  InsuranceClaimsExpertiseClass,
  InsuranceClaimsFeaturesClass,
  InsuranceClaimsLeadClass,
  InsuranceClaimsLineListClass,
  InsuranceclaimsMessages,
  InsuranceclaimsMessagesFilesClass,
  InsuranceClaimsOficinasConvencionadasClass,
  InsuranceClaimsPaymentListClass,
  InsuranceClaimsRepairClass,
  InsuranceClaimsResumeClass,
  InsuranceClaimsStatusTypesClass,
} from '../models/insuranceclaims-class';
import { FilesClass } from '../models/uploads-class';
import { HttpclienterrorService } from '../interceptor/httpclienterror.service';
import { ConfigStepperClass, ConfigStepperComponentClass } from '../models/dynamicpdf-class';
import { CrmCacheService, CrmSwrOptions } from './crm-cache.service';

@Injectable({
  providedIn: 'root',
})
export class InsuranceclaimsService {
  constructor(
    private http: HttpClient,
    public httpclienterrorService: HttpclienterrorService,
    private crmCache: CrmCacheService,
  ) {}

  GetInsuranceClaimsFeatures(): Observable<InsuranceClaimsFeaturesClass[]> {
    return this.http
      .get<InsuranceClaimsFeaturesClass[]>(environment.URL + `/insuranceClaims/GetInsuranceClaimsFeatures`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  SetInsuranceClaimsQuizConfigPDFComponentPosition(
    configStepperComponentClass: ConfigStepperComponentClass,
    uuid: string,
  ): Observable<ConfigStepperComponentClass[]> {
    configStepperComponentClass.UUIDLastChange = uuid;
    configStepperComponentClass.JSON = JSON.stringify(configStepperComponentClass.JSONArray);

    return this.http
      .post<
        ConfigStepperComponentClass[]
      >(environment.URL + `/insuranceClaims/SetInsuranceClaimsQuizConfigPDFComponentPosition`, configStepperComponentClass)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  SetInsuranceClaimsQuizConfigStepper(
    configStepperClass: ConfigStepperClass,
    uuid: string,
  ): Observable<ConfigStepperClass[]> {
    if (configStepperClass.extraStepper) {
      return;
    }

    configStepperClass.UUIDLastChange = uuid;

    return this.http
      .post<
        ConfigStepperClass[]
      >(environment.URL + `/insuranceClaims/SetInsuranceClaimsQuizConfigStepper`, configStepperClass)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  SetInsuranceClaimsQuizConfigStepperComponent(
    configStepperComponentClass: ConfigStepperComponentClass,
    uuid: string,
  ): Observable<ConfigStepperComponentClass[]> {
    if (configStepperComponentClass.extraStepper) {
      return;
    }

    configStepperComponentClass.UUIDLastChange = uuid;
    configStepperComponentClass.JSON = JSON.stringify(configStepperComponentClass.JSONArray);

    return this.http
      .post<
        ConfigStepperComponentClass[]
      >(environment.URL + `/insuranceClaims/SetInsuranceClaimsQuizConfigStepperComponent`, configStepperComponentClass)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsuranceClaimsQuizConfigPDFComponentPosition(
    idInsuranceClaimsQuizConfigPDF: number,
  ): Observable<ConfigStepperComponentClass[]> {
    return this.http
      .get<
        ConfigStepperComponentClass[]
      >(environment.URL + `/insuranceClaims/GetInsuranceClaimsQuizConfigPDFComponentPosition/${idInsuranceClaimsQuizConfigPDF}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  SetInsuranceClaimsQuizConfigPDF(
    insuranceClaimsQuizConfigPDFClass: FilesClass,
    uuid: string,
  ): Observable<FilesClass[]> {
    insuranceClaimsQuizConfigPDFClass.UUIDLastChange = uuid;

    return this.http
      .post<
        FilesClass[]
      >(environment.URL + `/insuranceClaims/SetInsuranceClaimsQuizConfigPDF`, insuranceClaimsQuizConfigPDFClass)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsuranceClaimsQuizConfigStepperComponent(
    idInsuranceClaimsQuizConfigStepper: number,
  ): Observable<ConfigStepperComponentClass[]> {
    return this.http
      .get<
        ConfigStepperComponentClass[]
      >(environment.URL + `/insuranceClaims/GetInsuranceClaimsQuizConfigStepperComponent/${idInsuranceClaimsQuizConfigStepper}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsuranceClaimsQuizConfigStepper(
    idCompanie: number,
    idProduct: number,
    idProductsTypes: number,
  ): Observable<ConfigStepperClass[]> {
    return this.http
      .get<
        ConfigStepperClass[]
      >(environment.URL + `/insuranceClaims/GetInsuranceClaimsQuizConfigStepper/${idCompanie}/${idProduct}/${idProductsTypes}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsuranceClaimsQuizConfigPDF(
    idCompanie: number,
    idProduct: number,
    idProductsTypes: number,
  ): Observable<FilesClass[]> {
    return this.http
      .get<
        FilesClass[]
      >(environment.URL + `/insuranceClaims/GetInsuranceClaimsQuizConfigPDF/${idCompanie}/${idProduct}/${idProductsTypes}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsuranceClaimsPolicyNumbers(
    idCompanie: number,
    idCustomer: number,
    idProduct: number,
    idProductsTypes: number,
    idInsurancePolicy: number,
  ): Observable<AuxClaimsPolicyNumbersClass[]> {
    return this.http
      .get<
        AuxClaimsPolicyNumbersClass[]
      >(environment.URL + `/insuranceClaims/GetInsuranceClaimsPolicyNumbers/${idCompanie}/${idCustomer}/${idProduct}/${idProductsTypes}/${idInsurancePolicy}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsCommentList(idInsuranceClaims: number): Observable<InsuranceClaimsCommentListClass[]> {
    return this.http
      .get<
        InsuranceClaimsCommentListClass[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsCommentList/${idInsuranceClaims}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsLineList(idInsuranceClaims: number): Observable<InsuranceClaimsLineListClass[]> {
    return this.http
      .get<
        InsuranceClaimsLineListClass[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsLineList/${idInsuranceClaims}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsPaymentList(idInsuranceClaims: number): Observable<InsuranceClaimsPaymentListClass[]> {
    return this.http
      .get<
        InsuranceClaimsPaymentListClass[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsPaymentList/${idInsuranceClaims}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsLead(idInsuranceClaims: number): Observable<InsuranceClaimsLeadClass[]> {
    return this.http
      .get<
        InsuranceClaimsLeadClass[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsLead/${idInsuranceClaims}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsResumo(idInsuranceClaims: number): Observable<InsuranceClaimsResumeClass[]> {
    return this.http
      .get<
        InsuranceClaimsResumeClass[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsResumo/${idInsuranceClaims}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsOficinasConvencionadas(
    idCompanie: number,
  ): Observable<InsuranceClaimsOficinasConvencionadasClass[]> {
    return this.http
      .get<
        InsuranceClaimsOficinasConvencionadasClass[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsOficinasConvencionadas/${idCompanie}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsRepair(idInsuranceClaimsMessage: number): Observable<InsuranceClaimsRepairClass[]> {
    return this.http
      .get<
        InsuranceClaimsRepairClass[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsRepair/${idInsuranceClaimsMessage}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsExpertise(idInsuranceClaimsMessage: number): Observable<InsuranceClaimsExpertiseClass[]> {
    return this.http
      .get<
        InsuranceClaimsExpertiseClass[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsExpertise/${idInsuranceClaimsMessage}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaSetInsuranceclaimsMessages(
    insuranceclaimsMessages: InsuranceclaimsMessages,
    uuid: string,
  ): Observable<InsuranceclaimsMessages[]> {
    insuranceclaimsMessages.UUIDLastChange = uuid;

    return this.http
      .post<
        InsuranceclaimsMessages[]
      >(environment.URL + `/insuranceClaims/ClientAreaSetInsuranceclaimsMessages`, insuranceclaimsMessages)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceclaimsMessages(idInsuranceClaimsMessage: number): Observable<InsuranceclaimsMessages[]> {
    return this.http
      .get<
        InsuranceclaimsMessages[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceclaimsMessages/${idInsuranceClaimsMessage}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceclaimsMessagesFiles(
    idInsuranceClaimsMessage: number,
  ): Observable<InsuranceclaimsMessagesFilesClass[]> {
    return this.http
      .get<
        InsuranceclaimsMessagesFilesClass[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceclaimsMessagesFiles/${idInsuranceClaimsMessage}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaCheckDatainsuranceclaims(idCustomer: number): Observable<Alert[]> {
    return this.http
      .get<Alert[]>(environment.URL + `/insuranceClaims/ClientAreaCheckDatainsuranceclaims/${idCustomer}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsId(idInsuranceClaims: number): Observable<InsuranceclaimsClass[]> {
    return this.http
      .get<
        InsuranceclaimsClass[]
      >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsId/${idInsuranceClaims}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsFiles(idInsuranceClaims: number, options?: CrmSwrOptions): Observable<FilesClass[]> {
    return this.crmCache.swr(
      `claims_files:${idInsuranceClaims}`,
      () =>
        this.http
          .get<any>(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsFiles/${idInsuranceClaims}`)
          .pipe(
            map((res: any) => {
              if (!res) return [];
              if (Array.isArray(res) && res.length > 0 && Array.isArray(res[0])) {
                return res[0];
              }
              if (Array.isArray(res)) {
                return res;
              }
              return [res];
            }),
            catchError(error => {
              const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
              return throwError(() => errorMessage);
            }),
          ),
      { persist: false, silentPrefetch: true, ...options },
    );
  }

  ClientAreaSetInsuranceClaimsFiles(idInsuranceClaims: number, files: any[]): Observable<any> {
    return this.http
      .post<any>(environment.URL + `/insuranceClaims/ClientAreaSetInsuranceClaimsFiles`, {
        idInsuranceClaims,
        insuranceClaimsFiles: files,
      })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ClientAreaGetInsuranceClaimsCustomerALL(
    idCustomer: number,
    options?: CrmSwrOptions,
  ): Observable<InsuranceclaimsClass[]> {
    return this.crmCache.swr(
      `claims_cust_all:${idCustomer}`,
      () =>
        this.http
          .get<
            InsuranceclaimsClass[]
          >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsCustomerALL/${idCustomer}`)
          .pipe(
            catchError(error => {
              const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
              return throwError(() => errorMessage);
            }),
          ),
      { persist: false, silentPrefetch: true, ...options },
    );
  }

  ClientAreaGetInsuranceClaimsCustomerALLWidget(
    idCustomer: number,
    options?: CrmSwrOptions,
  ): Observable<InsuranceclaimsClass[]> {
    return this.crmCache.swr(
      `claims_cust_widget:${idCustomer}`,
      () =>
        this.http
          .get<
            InsuranceclaimsClass[]
          >(environment.URL + `/insuranceClaims/ClientAreaGetInsuranceClaimsCustomerALLWidget/${idCustomer}`)
          .pipe(
            catchError(error => {
              const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
              return throwError(() => errorMessage);
            }),
          ),
      { persist: false, silentPrefetch: true, ...options },
    );
  }

  CommercialAreaGetInsuranceClaims(idCommercial: number): Observable<InsuranceclaimsClass[]> {
    return this.http
      .get<
        InsuranceclaimsClass[]
      >(environment.URL + `/insuranceClaims/CommercialAreaGetInsuranceClaims/${idCommercial}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsuranceClaimsCausesTypesALL(): Observable<InsuranceClaimsCausesTypesClass[]> {
    return this.http
      .get<InsuranceClaimsCausesTypesClass[]>(environment.URL + `/insuranceClaims/GetInsuranceClaimsCausesTypesALL`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsuranceClaimsCausesTypesID(idInsuranceClaimsCausesTypes: number): Observable<InsuranceClaimsCausesTypesClass[]> {
    return this.http
      .get<
        InsuranceClaimsCausesTypesClass[]
      >(environment.URL + `/insuranceClaims/GetInsuranceClaimsCausesTypesID/${idInsuranceClaimsCausesTypes}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsuranceClaimsStatusTypesALL(): Observable<InsuranceClaimsStatusTypesClass[]> {
    return this.http
      .get<InsuranceClaimsStatusTypesClass[]>(environment.URL + `/insuranceClaims/GetInsuranceClaimsStatusTypesALL`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  GetInsuranceClaimsAll(options?: CrmSwrOptions): Observable<InsuranceclaimsClass[]> {
    const isMediatorScope =
      typeof window !== 'undefined' &&
      window.location &&
      String(window.location.pathname || '').includes('/crm-mediador');
    const cacheKey = isMediatorScope ? 'claims_all_list_v2_med' : 'claims_all_list_v2_adm';

    return this.crmCache.swr(
      cacheKey,
      force => {
        const params: string[] = [];
        if (force) params.push('refresh=1');
        if (isMediatorScope) params.push('mediatorScope=1');
        const q = params.length > 0 ? `?${params.join('&')}` : '';
        return this.http
          .get<InsuranceclaimsClass[]>(environment.URL + `/insuranceClaims/GetInsuranceClaimsALL${q}`)
          .pipe(
            catchError(error => {
              const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
              return throwError(() => errorMessage);
            }),
          );
      },
      options,
    );
  }

  ClientAreaSetInsuranceClaims(
    insuranceclaimsClass: InsuranceclaimsClass,
    uuid: string,
  ): Observable<InsuranceclaimsClass> {
    insuranceclaimsClass.UUIDLastChange = uuid;

    return this.http
      .post<InsuranceclaimsClass>(
        environment.URL + `/insuranceClaims/ClientAreaSetInsuranceClaims`,
        insuranceclaimsClass,
      )
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  submitMobileSignature(payload: {
    sessionId?: string;
    token?: string;
    driver?: string;
    driverName?: string;
    signature: string;
    signedAt?: string;
  }): Observable<any> {
    return this.http.post<any>(environment.URL + `/insuranceClaims/mobile-signature/session`, payload).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  getMobileSignatureSession(sessionIdOrToken: string): Observable<any> {
    return this.http.get<any>(environment.URL + `/insuranceClaims/mobile-signature/session/${sessionIdOrToken}`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  sendMobileSignatureEmail(payload: {
    sessionId: string;
    driver: 'A' | 'B';
    driverName: string;
    driverEmail: string;
    accidentDate?: string;
    incidentLocation?: string;
    driverPlate?: string;
    otherPlate?: string;
    driverDamages?: string;
    vehicleAPlate?: string;
    vehicleBPlate?: string;
    origin?: string;
  }): Observable<any> {
    return this.http.post<any>(environment.URL + `/insuranceClaims/mobile-signature/send-email`, payload).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // ── EMAILS DE SEGURADORAS & REVISÃO MANUAL (Feedback #494) ──
  getInsurerEmails(params?: { status?: string; idInsuranceClaims?: number }): Observable<any> {
    const queryParts: string[] = [];
    if (params?.status) queryParts.push(`status=${encodeURIComponent(params.status)}`);
    if (params?.idInsuranceClaims)
      queryParts.push(`idInsuranceClaims=${encodeURIComponent(String(params.idInsuranceClaims))}`);
    const q = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return this.http.get<any>(environment.URL + `/insuranceClaims/insurer-emails${q}`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  syncInsurerEmails(): Observable<any> {
    return this.http.post<any>(environment.URL + `/insuranceClaims/insurer-emails/sync`, {}).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  associateInsurerEmail(emailId: string, idInsuranceClaims: number, uuid?: string, idUser?: number): Observable<any> {
    return this.http
      .post<any>(environment.URL + `/insuranceClaims/insurer-emails/associate`, {
        emailId,
        idInsuranceClaims,
        uuid,
        idUser,
      })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ignoreInsurerEmail(emailId: string, restore = false): Observable<any> {
    return this.http
      .post<any>(environment.URL + `/insuranceClaims/insurer-emails/ignore`, {
        emailId,
        restore,
      })
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  ingestInsurerEmail(payload: {
    fromAddress?: string;
    fromEmail?: string;
    fromName?: string;
    subject: string;
    textBody: string;
    date?: string;
    idInsuranceClaims?: number;
    attachments?: Array<{
      filename?: string;
      fileName?: string;
      contentType?: string;
      size?: number;
      contentBase64?: string;
      base64Data?: string;
      savedRelativePath?: string;
    }>;
  }): Observable<any> {
    return this.http.post<any>(environment.URL + `/insuranceClaims/insurer-emails/ingest`, payload).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  getCarglassQivByClaimId(
    idInsuranceClaims: number,
  ): Observable<{ sucesso: boolean; dados: any; temProcessoCarglass: boolean }> {
    return this.http
      .get<{
        sucesso: boolean;
        dados: any;
        temProcessoCarglass: boolean;
      }>(environment.URL + `/carglass-qiv/sinistro/${idInsuranceClaims}`)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  syncCarglassQivByClaimId(idInsuranceClaims: number): Observable<{ sucesso: boolean; mensagem: string; dados: any }> {
    return this.http
      .post<{
        sucesso: boolean;
        mensagem: string;
        dados: any;
      }>(environment.URL + `/carglass-qiv/sincronizar/${idInsuranceClaims}`, {})
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  sendCarglassQiv(payload: {
    nomeCliente: string;
    contactoTelefonico: string;
    matricula: string;
    origemPedido?: string;
    idUtilizador?: number;
    idInsuranceClaims?: number;
  }): Observable<any> {
    return this.http.post<any>(environment.URL + `/carglass-qiv/enviar-sinistro`, payload).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  // ── AGENDAMENTO AUTOMÁTICO (CRON) CARGLASS QIV ONLINE (EXCLUSIVO ADMIN) ──
  getCarglassQivCronStatus(): Observable<{ sucesso: boolean; dados: any }> {
    return this.http.get<{ sucesso: boolean; dados: any }>(environment.URL + `/carglass-qiv/cron/status`).pipe(
      catchError(error => {
        const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
        return throwError(() => errorMessage);
      }),
    );
  }

  saveCarglassQivCronConfig(
    config: Record<string, any>,
  ): Observable<{ sucesso: boolean; mensagem: string; config: any; dados: any }> {
    return this.http
      .post<{
        sucesso: boolean;
        mensagem: string;
        config: any;
        dados: any;
      }>(environment.URL + `/carglass-qiv/cron/config`, config)
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }

  runCarglassQivCronManual(): Observable<{ sucesso: boolean; mensagem: string; resultado: any; dados: any }> {
    return this.http
      .post<{
        sucesso: boolean;
        mensagem: string;
        resultado: any;
        dados: any;
      }>(environment.URL + `/carglass-qiv/cron/run`, {})
      .pipe(
        catchError(error => {
          const errorMessage: string = HttpclienterrorService.getServerErrorMessage(error);
          return throwError(() => errorMessage);
        }),
      );
  }
}
